/**
 * The frontier a draft will be sent against (RFC 0302 S6).
 *
 * S3 made a post carry the heads the composer could see. That is right at the
 * instant of sending and wrong for the minute before it: the thread re-renders
 * whenever anything lands, so a message from the other person arriving while
 * you type would silently become the parent of what you are writing. Your reply
 * would then read as a reply to THEM, in the log and in every transcript built
 * from it, which is 3e's "another person's newer message does not redirect what
 * you send" broken in the one place nobody would look.
 *
 * So the frontier is PINNED when a draft opens and released when it is sent.
 * Two people typing at once produce two posts that are siblings of the same
 * parent, which is what actually happened, and a branch either of them meant to
 * continue stays the one they were looking at.
 *
 * A closure over one nullable value rather than React state: this must not
 * cause a render (it would re-render the box being typed in on every
 * keystroke), and it must be testable without mounting a thread.
 */

export interface BranchFrontier {
  /**
   * The draft changed. An empty draft releases the pin — the person cleared the
   * box, so whatever they write next is a reply to the thread as it stands now,
   * not as it stood before they changed their mind.
   */
  draftChanged(text: string, visible: readonly string[]): void
  /**
   * The heads to send against, and release the pin. Falls back to the live
   * frontier for a send with no draft behind it: a slash command, a one-tap
   * answer, a test.
   */
  take(visible: readonly string[]): string[]
}

export function makeBranchFrontier(): BranchFrontier {
  let pinned: string[] | null = null
  return {
    draftChanged(text, visible) {
      if (text.trim().length === 0) {
        pinned = null
        return
      }
      // Only the FIRST keystroke pins. Every one after it would re-pin to a
      // frontier that has moved, which is the bug this exists to prevent.
      if (pinned === null) pinned = [...visible]
    },
    take(visible) {
      const heads = pinned ?? [...visible]
      pinned = null
      return heads
    },
  }
}
